import React, { ReactNode } from "react";
import { useTranslations } from "@jugl-web/utils";
import { InteractiveContainer, TextInput } from "@jugl-web/ui-kit";
import { ColorPickerPopover } from "@jugl-web/ui-components/web/ColorPickerPopover/ColorPickerPopover";
import { Controller, useFormContext } from "react-hook-form";
import { Separator } from "../../../../components/Separator";
import { TabHeader } from "../../../../components/TabHeader";
import { AppearanceSettingsTabShell } from "../AppearanceSettingsTabShell";
import { ChatBotChatAvatarPicker } from "../../../../components/ChatBotChatAvatarPicker";
import { InfoIndicator } from "../../../../components/InfoIndicator";
import { LiveChatAppSettingsFormValues } from "../../types";
import { ChatBotAgentAvatarPicker } from "../../../WebChatBotAccountScreen/components/ChatBotAgentAvatarPicker";

export const GeneralInfoTabContent: React.FC = () => {
  const { t } = useTranslations();

  return (
    <AppearanceSettingsTabShell tabId="generalInfo">
      <TabHeader
        title={t({
          id: "client-support-page.general-info",
          defaultMessage: "General Info",
        })}
        subtitle={t({
          id: "client-support-page.general-info-description",
          defaultMessage: "Set the naming and style of your Live Chat",
        })}
      />
      <Separator className="my-8" />
      <GeneralInfoTabContentInner />
    </AppearanceSettingsTabShell>
  );
};

const GeneralInfoTabContentInner: React.FC = () => {
  const { t } = useTranslations();
  const { control } = useFormContext<LiveChatAppSettingsFormValues>();

  return (
    <div className="flex flex-col gap-14">
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <span className="font-secondary text-sm text-dark-700">
            {t({
              id: "client-support-page.names-branding-section",
              defaultMessage: "Names & Branding",
            })}
          </span>
          <InfoIndicator
            message={t(
              {
                id: "client-support-page.naming-section-info",
                defaultMessage:
                  "<highlightedText>Browser Tab Title & Favicon</highlightedText> - the name and icon of the Live Chat page when it's opened in the Browser",
              },
              {
                highlightedText: (text: ReactNode) => (
                  <span className="font-semibold">{text}</span>
                ),
              }
            )}
          />
        </div>
        <div className="flex items-start gap-4">
          <Controller
            control={control}
            name="browserTabTitle"
            render={({ field: { onChange, ...props } }) => (
              <TextInput
                labelPosition="border-overlap"
                label={t({
                  id: "client-support-page.browser-tab-title-favicon",
                  defaultMessage: "Browser Tab Title & Favicon",
                })}
                placeholder={t({ id: "common.enter", defaultMessage: "Enter" })}
                classNames={{
                  wrapperClassName: "w-full",
                  inputClassName: "text-[14px]",
                }}
                onChange={(e) => onChange(e.target.value.trimStart())}
                {...props}
              />
            )}
          />
          <div className="relative top-[9px]">
            <Controller
              control={control}
              name="faviconUrl"
              render={({ field: { value, onChange } }) => (
                <ChatBotChatAvatarPicker
                  image={value}
                  onImageChange={onChange}
                />
              )}
            />
          </div>
        </div>
        <div className="flex items-start gap-4">
          <Controller
            control={control}
            name="businessName"
            render={({ field: { onChange, ...props } }) => (
              <TextInput
                labelPosition="border-overlap"
                label={t({
                  id: "client-support-page.business-name-logo",
                  defaultMessage: "Business Name & Logo",
                })}
                placeholder={t({ id: "common.enter", defaultMessage: "Enter" })}
                classNames={{
                  wrapperClassName: "w-full",
                  inputClassName: "text-[14px]",
                }}
                onChange={(e) => onChange(e.target.value.trimStart())}
                {...props}
              />
            )}
          />
          <div className="relative top-[9px]">
            <Controller
              control={control}
              name="businessLogoUrl"
              render={({ field: { value, onChange } }) => (
                <ChatBotChatAvatarPicker
                  image={value}
                  onImageChange={onChange}
                />
              )}
            />
          </div>
        </div>
        <div className="flex items-start gap-4">
          <Controller
            control={control}
            name="agentName"
            render={({ field: { onChange, ...props } }) => (
              <TextInput
                labelPosition="border-overlap"
                label={t({
                  id: "client-support-page.agent-name-pic",
                  defaultMessage: "Agent Name & Pic",
                })}
                placeholder={t({ id: "common.enter", defaultMessage: "Enter" })}
                classNames={{
                  wrapperClassName: "w-full",
                  inputClassName: "text-[14px]",
                }}
                onChange={(e) => onChange(e.target.value.trimStart())}
                {...props}
              />
            )}
          />
          <div className="relative top-[9px]">
            <Controller
              control={control}
              name="agentImage"
              render={({ field: { value, onChange } }) => (
                <ChatBotAgentAvatarPicker
                  avatar={value}
                  onAvatarChange={onChange}
                />
              )}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-6">
        <span className="font-secondary text-sm text-dark-700">
          {t({
            id: "cx-ai-agents-page.chat-style",
            defaultMessage: "Chat Style",
          })}
        </span>
        <Controller
          control={control}
          name="senderMessageBackgroundColor"
          render={({ field: { value, onChange } }) => (
            <ColorPickerPopover
              title={value}
              color={value}
              onColorChange={onChange}
              renderTrigger={({ Trigger, triggerRef }) => (
                <Trigger
                  ref={triggerRef}
                  as={InteractiveContainer}
                  className="flex h-[60px] items-center gap-3 rounded-[10px] border border-solid border-grey-400 px-5 font-secondary text-sm text-black"
                >
                  <div
                    className="h-6 w-6 rounded-md"
                    style={{ backgroundColor: value }}
                  />
                  <span>
                    {t({
                      id: "cx-ai-agents-page.sender-message-color",
                      defaultMessage: "Sender message color:",
                    })}
                  </span>
                  <span>{value}</span>
                </Trigger>
              )}
            />
          )}
        />
      </div>
    </div>
  );
};
